import { QueryClient, UseMutationResult } from '@tanstack/react-query'
import {
  TRPCClientError,
  TRPCClientErrorLike,
  createTRPCClient,
  httpBatchLink,
  httpLink,
  loggerLink,
  splitLink,
} from '@trpc/client'
import { createTRPCContext, createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import pluralize from 'pluralize'
import { getConfigValue } from '~/helpers/envHelpers'
import { isJSONString } from '~/helpers/jsonHelpers'
import {
  getAuthToken,
  getOrganizationAliasFromUrl,
  getOrganizationIdFallbackFromUrl,
} from '~/helpers/sessionHelpers'
import { logger } from '~/services/logger'

import type {
  AppRouter,
  RouterOutput as AppRouterOutput,
  RouterInput,
} from '../../../../packages/trpc/router'

interface DefaultErrorData {
  code: string
  httpStatus: number
  path: string[]
  message: string
  type: string
}

const isTRPCValidationError = (messages: unknown): messages is DefaultErrorData[] =>
  Array.isArray(messages)

export class TRPCValidationError {
  messages: Record<string, string> = { global: 'unknown error' }

  constructor(err: unknown) {
    this.getFieldLevelMessages = this.getFieldLevelMessages.bind(this)

    try {
      if (err instanceof TRPCClientError) {
        const messages = isJSONString(err.message) ? JSON.parse(err.message) : err.message
        if (isTRPCValidationError(messages)) {
          this.messages = messages.reduce(
            (acc, error) => ({
              ...acc,
              [error.path.join('.')]: error.message,
            }),
            {},
          )
        } else if (typeof messages === 'string') {
          this.messages = { global: messages }
        }
      }
    } catch (e) {
      logger.error(`TRPCValidationError error 002: ${err}`)
    }
  }

  public getFieldLevelMessages() {
    return this.messages
  }

  public getFriendlyMessage() {
    const numberOfErrors = Object.keys(this.messages).length
    return numberOfErrors > 1
      ? `${numberOfErrors} ${pluralize('error', numberOfErrors)} found`
      : Object.values(this.messages)[0]
  }
}

const unavailableHttpStatuses = new Set([502, 503, 504])
const networkErrorMessages = [
  'econnrefused',
  'failed to fetch',
  'fetch failed',
  'load failed',
  'network error',
  'networkerror',
]

const getHttpStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return undefined

  const { data } = error
  if (typeof data !== 'object' || data === null || !('httpStatus' in data))
    return undefined

  return typeof data.httpStatus === 'number' ? data.httpStatus : undefined
}

const getErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return undefined

  const { data } = error
  if (typeof data !== 'object' || data === null || !('code' in data)) return undefined

  return typeof data.code === 'string' ? data.code : undefined
}

export const isServiceUnavailableError = (error: unknown): boolean => {
  if (!error) return false
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true

  const httpStatus = getHttpStatus(error)
  if (httpStatus !== undefined && unavailableHttpStatuses.has(httpStatus)) return true

  return (
    error instanceof Error &&
    networkErrorMessages.some((message) => error.message.toLowerCase().includes(message))
  )
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on tRPC client errors (4xx).
        const errorCode = getErrorCode(error)
        if (errorCode && errorCode !== 'INTERNAL_SERVER_ERROR') {
          return false
        }
        // Retry up to 3 times for server errors (5xx) and transport failures.
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
})

function headers() {
  const token = getAuthToken()
  const headers: Record<string, string> = {
    'happitu-organization-alias': getOrganizationAliasFromUrl(),
    'happitu-organization-id': getOrganizationIdFallbackFromUrl(),
  }
  if (token) headers['authorization'] = `Bearer ${token}`
  return headers
}

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    loggerLink({
      enabled(opts) {
        return opts.direction === 'down' && opts.result instanceof Error
      },
    }),
    splitLink({
      condition(op) {
        // check for context property `skipBatch`
        return Boolean(op.context.skipBatch)
      },
      // when condition is true, use normal request
      true: httpLink({
        url: getConfigValue('VITE_TRPC_URL'),
        headers,
        methodOverride: 'POST',
      }),
      // when condition is false, use batching
      false: httpBatchLink({
        url: getConfigValue('VITE_TRPC_URL'),
        headers,
        methodOverride: 'POST',
      }),
    }),
  ],
})
export const trpcProxy = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
})

// Helper type to extract nested values via dot notation (e.g., 'queries.upsert')
type DeepValue<T, K extends string> = K extends `${infer First}.${infer Rest}`
  ? First extends keyof T
    ? DeepValue<T[First], Rest>
    : never
  : K extends keyof T
    ? T[K]
    : never
// Main utility - use like: MutationOf<'queries.upsert'>
export type MutationOf<TPath extends string> = UseMutationResult<
  DeepValue<RouterOutput, TPath>,
  TRPCClientErrorLike<AppRouter>,
  DeepValue<RouterInput, TPath>,
  unknown
>

export type RouterOutput = AppRouterOutput
export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>()
